import { z } from 'zod';
import { HttpMethodSchema } from '@terra/shared-schemas-orchestrator';
import {
  AssetPreviewSchema,
  AssetsDateRangeBoundarySchema,
  AssetsParamsSchema,
} from './assets.schema';

export const AssetsSearchTypeSchema = z
  .enum(['endpoints', 'agents', 'allEndpoints', 'webpages'])
  .describe(
    'endpoints returns web endpoints, agents returns AI agent endpoints, allEndpoints returns both, webpages returns web pages',
  );

export const AssetsApiTypeSchema = z.enum([
  'REST',
  'GraphQL',
  'SOAP',
  'XML',
  'MCP',
  'WebSocket',
  'gRPC',
]);

export const AssetsSearchOrderByFieldSchema = z.enum([
  'updatedAt',
  'createdAt',
  'url',
  'name',
  'domain',
  'method',
  'detections',
  'roles',
  'sources',
  'apiType',
  'relatedWebpages',
  'relatedEndpoints',
]);

export const AssetsSearchOrderBySchema = z.object({
  field: AssetsSearchOrderByFieldSchema,
  order: z.enum(['asc', 'desc']),
});

export const AssetsSearchGroupBySchema = z
  .enum(['domain', 'method'])
  .describe(
    'Group results by domain or HTTP method. Results are ordered by the group key first and each group count is returned. Method grouping applies to endpoints only.',
  );

export const AssetsSearchGroupSchema = z.object({
  key: z.string(),
  count: z.int().nonnegative(),
});

export const AssetsDomainFilterSchema = z.object({
  host: z.string(),
  pathPrefix: z.string().optional(),
  method: z.enum(['GET', 'POST', 'PUT', 'PATCH', 'DELETE']).optional(),
});

export const AssetsSearchBodySchema = z.object({
  type: AssetsSearchTypeSchema,
  url: z.string().optional(),
  methods: HttpMethodSchema.array().optional(),
  apiTypes: AssetsApiTypeSchema.array()
    .optional()
    .describe('Return only endpoints whose API type is in this list'),
  roles: z.string().array().optional(),
  roleFilterType: z
    .enum(['multiple', 'specific'])
    .optional()
    .default('multiple'),
  coverage: z.enum(['all', 'reviewed', 'unreviewed']).optional(),
  endpointSecurityType: z.enum(['vulnerable', 'sensitive', 'clear']).optional(),
  detectionView: z.enum(['active', 'all']).optional().default('active'),
  showMissingCode: z.boolean().optional(),
  withCodeGraph: z
    .boolean()
    .optional()
    .describe('When true, return only endpoints with an associated code graph'),
  domainFilters: AssetsDomainFilterSchema.array().optional(),
  crawlerSessionId: z.string().optional(),
  createdStartDate: z.coerce.date().optional(),
  createdEndDate: z.coerce.date().optional(),
  updatedStartDate: z.coerce.date().optional(),
  updatedEndDate: z.coerce.date().optional(),
  dateRangeBoundary: AssetsDateRangeBoundarySchema.optional(),
  orderBy: AssetsSearchOrderBySchema.array()
    .optional()
    .describe(
      'Sort order. While groupBy is set, applies within each group; groups stay ordered by key.',
    ),
  groupBy: AssetsSearchGroupBySchema.optional(),
  limit: z.coerce.number().int().positive().max(200).optional(),
  skip: z.coerce.number().int().nonnegative().optional(),
  lifecycleState: z
    .enum(['active', 'archived', 'all'])
    .optional()
    .describe('Filter by asset lifecycle state (active, archived, or all)'),
});

export const AssetsSearchMetricsBodySchema = AssetsSearchBodySchema.omit({
  endpointSecurityType: true,
  orderBy: true,
  groupBy: true,
  limit: true,
  skip: true,
}).describe(
  'Same filters as searchAssets. Role counts ignore the role filter and hasUnavailableCodeGraphs ignores the code graph filters so they can drive filter options.',
);
export const AssetPreviewWithMetricsSchema = AssetPreviewSchema;

export const AssetsSearchReturnSchema = z.object({
  assets: z.array(AssetPreviewWithMetricsSchema),
  totalCount: z.int().nonnegative(),
  groups: z
    .array(AssetsSearchGroupSchema)
    .optional()
    .describe('Asset count per group key across all pages when groupBy is set'),
});

export const AssetsRoleCountSchema = z.object({
  role: z.string(),
  count: z.int().nonnegative(),
});

export const AssetsSecurityCountsSchema = z.object({
  vulnerable: z.int().nonnegative(),
  sensitive: z.int().nonnegative(),
  clear: z.int().nonnegative(),
});

export const AssetsEndpointTypeCountsSchema = z.object({
  endpoints: z.int().nonnegative(),
  agents: z.int().nonnegative(),
});

export const AssetsSearchMetricsReturnSchema = z.object({
  total: z.int().nonnegative(),
  roles: z.array(AssetsRoleCountSchema),
  securityCounts: AssetsSecurityCountsSchema,
  endpointTypeCounts: AssetsEndpointTypeCountsSchema,
  hasUnavailableCodeGraphs: z.boolean(),
});

export const AssetExportRowSchema = z.object({
  id: z.string(),
  url: z.string(),
  method: z.string(),
  domain: z.string(),
  path: z.string(),
  credentialRoles: z.string(),
  createdAt: z.coerce.date().optional(),
  updatedAt: z.coerce.date().optional(),
});

export const AssetsSearchExportReturnSchema = z.object({
  assets: z.array(AssetExportRowSchema),
  truncated: z.boolean(),
});

export type AssetsSearchBody = z.infer<typeof AssetsSearchBodySchema>;
export type AssetsSearchOrderByField = z.infer<
  typeof AssetsSearchOrderByFieldSchema
>;
export type AssetsSearchOrderBy = z.infer<typeof AssetsSearchOrderBySchema>;
export type AssetsSearchGroupBy = z.infer<typeof AssetsSearchGroupBySchema>;
export type AssetsSearchGroup = z.infer<typeof AssetsSearchGroupSchema>;
export type AssetsSearchMetricsBody = z.infer<
  typeof AssetsSearchMetricsBodySchema
>;
export type AssetsSearchReturn = z.infer<typeof AssetsSearchReturnSchema>;
export type AssetsSearchMetricsReturn = z.infer<
  typeof AssetsSearchMetricsReturnSchema
>;
export type AssetsSearchExportReturn = z.infer<
  typeof AssetsSearchExportReturnSchema
>;
export type AssetExportRow = z.infer<typeof AssetExportRowSchema>;

export { AssetsParamsSchema };
